import { formatDate } from '@/shared/lib/date-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { ShiftAssignmentKind } from '../types/shift';
import type {
  ShiftAssignment,
  TeamShiftAssignmentItem,
  UserShiftAssignmentItem,
} from '../types/shift';

export interface RemoveTeamAssignmentArgs {
  teamId: number;
  startDate: string;
  endDate: string;
}

interface ShiftAssignmentsProps {
  assignments: ShiftAssignment[];
  onRemoveTeamAssignment: (args: RemoveTeamAssignmentArgs) => void;
  onDeleteAssignment: (id: number) => void;
  onAssignToTeam: () => void;
}

export function ShiftAssignments({
  assignments,
  onRemoveTeamAssignment,
  onDeleteAssignment,
  onAssignToTeam,
}: ShiftAssignmentsProps) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Team Assignments</h3>
        <Badge
          variant="outline"
          className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 border-blue-200 dark:border-blue-800"
        >
          {assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}
        </Badge>
      </div>

      {assignments.length === 0 ? (
        <EmptyState onAssignToTeam={onAssignToTeam} />
      ) : (
        <div className="space-y-3">
          {assignments.map((assignment) =>
            assignment.kind === ShiftAssignmentKind.TEAM ? (
              <TeamAssignment
                key={`team-${assignment.teamId}-${assignment.startDate}-${assignment.endDate}`}
                assignment={assignment}
                onRemove={onRemoveTeamAssignment}
              />
            ) : (
              <UserAssignment
                key={`user-${assignment.id}`}
                assignment={assignment}
                onRemove={onDeleteAssignment}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function TeamAssignment({
  assignment,
  onRemove,
}: {
  assignment: TeamShiftAssignmentItem;
  onRemove: (args: RemoveTeamAssignmentArgs) => void;
}) {
  const { teamName, memberCount, startDate, endDate } = assignment;
  return (
    <AssignmentRow
      title={teamName}
      detail={`${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
      dateRange={`${formatDate(startDate)} to ${formatDate(endDate)}`}
      onRemove={() => onRemove({ teamId: assignment.teamId, startDate, endDate })}
    />
  );
}

function UserAssignment({
  assignment,
  onRemove,
}: {
  assignment: UserShiftAssignmentItem;
  onRemove: (id: number) => void;
}) {
  const { userName, startDate, endDate } = assignment;
  return (
    <AssignmentRow
      title={userName}
      dateRange={`${formatDate(startDate)} to ${formatDate(endDate)}`}
      onRemove={() => onRemove(assignment.id)}
    />
  );
}

function AssignmentRow({
  title,
  detail,
  dateRange,
  onRemove,
}: {
  title: string;
  detail?: string;
  dateRange: string;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-foreground">{title}</div>
        <div className="text-sm text-muted-foreground">
          {detail ? `${detail} · ` : ''}
          {dateRange}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Remove assignment"
        className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
        onClick={onRemove}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function EmptyState({ onAssignToTeam }: { onAssignToTeam: () => void }) {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>No assignments for this shift yet</p>
      <Button variant="outline" className="mt-4" onClick={onAssignToTeam}>
        <Plus className="w-4 h-4 mr-2" />
        Assign to Team
      </Button>
    </div>
  );
}
